// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {fetchEffectData} from 'Utils/react-utils';
import {fetchAndFilterInactiveOperators} from 'OnLabor/workboard/utils/fetch-operators';
import {getOvertimeThresholds} from 'OnLabor/workboard/utils/overtime-status';
import {sendSMS} from 'Services/sms-service';
import {
    getGroupedFormattedOperators,
    getFormattedOperators
} from 'OnLabor/workboard/utils/operator-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {Accordion, AccordionDetails, AccordionSummary, AssistanceTooltip, Box, IconButton, Link, MultiSelect, Stack, TextField, Typography} from '@deere/isg.component-library';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from '../../../../constants/toast-constants';
import {IconHelp, Send} from '@deere/icons';

const MAX_MESSAGE_LENGTH = 144;
const MIN_PHONE_DIGITS = 10;

function formatPhoneNumber(phoneNumber) {
    const digits = String(phoneNumber).replace(/\D/g, '');

    if (digits.length < MIN_PHONE_DIGITS) {
        return null;
    }
    const last10 = digits.slice(-MIN_PHONE_DIGITS);

    return `1${last10}`;
}

function sendMessagesToOperators(operatorsWithPhones, msg, addToast, translations, setMsg, setSelectedOperators, setLoading) {
    return async function () {
        const results = await Promise.allSettled(
            operatorsWithPhones.map(({
                operator,
                phoneNumber
            }) =>
                sendSMS({
                    to: phoneNumber,
                    body: msg
                }).then(() => ({
                    success: true,
                    operator
                })).catch(() => ({
                    success: false,
                    operator
                }))
            )
        );

        const successCount = results.filter((r) => r.status === 'fulfilled' && r.value.success).length;
        const failedOperators = results
            .filter((r) => r.status === 'fulfilled' && !r.value.success)
            .map((r) => r.value.operator);

        if (successCount > 0) {
            addToast({
                title: translations.SMS_MESSAGING_SUCCESS_TITLE,
                message: replaceTranslationNames(translations.SMS_MESSAGING_SUCCESS_BODY, {
                    '0': successCount
                }),
                type: TOAST_TYPE.SUCCESS
            });
            setMsg('');
            setSelectedOperators([]);
        }

        if (failedOperators.length > 0) {
            const operatorNames = failedOperators
                .map((op) => `${op.firstName} ${op.lastName}`)
                .join(', ');

            addToast({
                title: translations.SMS_MESSAGING_ERROR_TITLE,
                message: operatorNames,
                type: TOAST_TYPE.ERROR
            });
        }

        setLoading(false);
    };
}

const LINK_STYLES = {
    color: '#367c2b',
    textDecoration: 'underline',
    textAlign: 'right'
};

function getMultiSelectLabels(translations) {
    return {
        placeholder: translations.selectTeamMembersToAssign,
        multiSelect: translations.IWP_SELECT_NO_OPERATORS,
        emptyList: translations.IWP_SELECT_NO_OPERATORS,
        allSelect: translations.SELECT_ALL,
        selectAllItems: translations.SELECT_ALL
    };
}

function mapOperatorToPhone(operator) {
    const phoneNumber = operator?.authenticatedUser?.phoneNumber?.phoneNumber || operator?.phoneNumber?.phoneNumber;
    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (formattedPhone) {
        return {
            operator,
            phoneNumber: formattedPhone
        };
    }

    return null;
}

function Messaging(props) {
    const {
        membership,
        orgId,
        translations,
        userRoles
    } = props;
    const [operators, setOperators] = React.useState([]);
    const [selectedOperators, setSelectedOperators] = React.useState([]);
    const [msg, setMsg] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const {addToast} = useToast();
    const MULTI_SELECT_LABELS = getMultiSelectLabels(translations);
    const remainingChars = MAX_MESSAGE_LENGTH - msg.length;
    const charactersText = replaceTranslationNames(translations.ONLINK_SMS_CHAR_REMAINING, {
        '0': remainingChars.toString(),
        '1': MAX_MESSAGE_LENGTH.toString()
    });

    const isDisabled = React.useMemo(() => {
        if (msg.length === 0) {
            return true;
        }
        if (selectedOperators.length === 0) {
            return true;
        }
        return false;
    }, [selectedOperators, msg]);

    const handleClick = async () => {
        setLoading(true);

        const operatorsWithPhones = selectedOperators
            .map((operatorId) => operators.find((op) => op.id === operatorId))
            .filter(Boolean)
            .map(mapOperatorToPhone)
            .filter(Boolean);

        if (operatorsWithPhones.length === 0) {
            addToast({
                message: translations.ONLINK_SMS_NO_PHONE_NUMBERS,
                type: TOAST_TYPE.ERROR
            });
            setLoading(false);
            return;
        }

        const sendingTitle = replaceTranslationNames(translations.SMS_MESSAGING_SENDING_TITLE, {
            '0': operatorsWithPhones.length
        });

        addToast({
            message: sendingTitle,
            type: TOAST_TYPE.INFO
        });

        const sendMessages = sendMessagesToOperators(operatorsWithPhones, msg, addToast, translations, setMsg, setSelectedOperators, setLoading);

        await sendMessages();
    };

    const {
        overtimeReachedThreshold,
        overtimeWarningThreshold
    } = getOvertimeThresholds(
        membership.properties.workboard_weekly_work_hours,
        membership.properties.workboard_employee_daily_hours_limit
    );
    const operatorItems = React.useMemo(() => {
        if (!membership.isMigrated) {
            return {
                items: getFormattedOperators({
                    enablePhoneCheck: true,
                    operators,
                    overtimeReachedThreshold,
                    overtimeWarningThreshold,
                    showIcon: true,
                    translations
                }, true)
            };
        }

        const {groupedOperators: groups} = getGroupedFormattedOperators({
            enablePhoneCheck: true,
            operators,
            overtimeReachedThreshold,
            overtimeWarningThreshold,
            showIcon: true,
            translations
        }, true);

        return {
            groups,
            items: [],
            groupOptions: {
                collapseAll: true
            }
        };
    }, [
        membership.isMigrated, operators,
        overtimeReachedThreshold, overtimeWarningThreshold, userRoles
    ]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const teamMembers = await fetchAndFilterInactiveOperators(orgId);

        if (isMounted()) {
            setOperators(() => {
                return [...teamMembers];
            });
            setLoading(false);
        }
    }), []);

    return (
        <Accordion className='messaging_accordion'>
            <AccordionSummary>{translations.ONLINK_GROUP_MESSAGING}</AccordionSummary>
            <AccordionDetails>
                <div className='messaging_content'>
                    <div className='messaging_header'>
                        <div className='messaging_label'>
                            <Typography
                                display='block'
                                variant='labelMedium'
                            >
                                {translations.my_team_members_header}
                            </Typography>
                            <AssistanceTooltip
                                allowClickPropagation
                                body={
                                    <>
                                        <Box
                                            sx={{
                                                paddingBlock: '8px',
                                                paddingInline: '16px'
                                            }}
                                        >
                                            <Typography
                                                display='block'
                                                variant='labelLargeSemibold'
                                            >
                                                {translations.ONLINK_SMS_TOOLTIP_TITLE}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                paddingBlock: '8px',
                                                paddingInline: '16px'
                                            }}
                                        >
                                            <Stack
                                                direction={'column'}
                                                gap={'8px'}
                                            >
                                                <Typography
                                                    display={'block'}
                                                    variant={'bodyMedium'}
                                                >
                                                    {translations.ONLINK_SMS_TOOLTIP_MEMBERS}
                                                </Typography>
                                                <Typography
                                                    display={'block'}
                                                    variant={'bodyMedium'}
                                                >
                                                    {`${translations.ONLINK_SMS_TOOLTIP_GROUP} `}
                                                    <Link
                                                        href={`/organizations/${orgId}/team/team-members`}
                                                        sx={LINK_STYLES}
                                                    >
                                                        {translations.TEAM_MANAGER_v2}
                                                    </Link>
                                                    {'.'}
                                                </Typography>
                                                <Link
                                                    href='https://onlinesupport.turf.deere.com/en/search?page=1&term=color%20group%20in%20pro%20golf'
                                                    sx={LINK_STYLES}
                                                    variant='bodyMedium'
                                                >
                                                    {translations.ONLINK_SMS_TOOLTIP_HELP}
                                                </Link>
                                            </Stack>
                                        </Box>
                                    </>
                                }
                                className={'msg-tooltip'}
                                placement='bottom'
                            >
                                <IconHelp
                                    size='sm'
                                />
                            </AssistanceTooltip>
                        </div>
                        <MultiSelect
                            {...operatorItems}
                            className={'msg_select'}
                            disableSorting={true}
                            labels={MULTI_SELECT_LABELS}
                            loading={loading}
                            onChange={(newIds) => setSelectedOperators(newIds)}
                            selectedIds={selectedOperators}
                            title='Message operators'
                        />
                    </div>
                    <div className='messaging_body'>
                        <TextField
                            error={remainingChars === 0}
                            fullWidth
                            helperText={charactersText}
                            minRows={5}
                            multiline
                            onChange={(e) => setMsg(e.target.value)}
                            slotProps={{
                                htmlInput: {
                                    maxLength: MAX_MESSAGE_LENGTH
                                }
                            }}
                            value={msg}
                        />
                        <div className='messaging_btn'>
                            <IconButton
                                disabled={isDisabled}
                                loading={loading}
                                onClick={handleClick}
                            >
                                {!loading && <Send
                                    primary='#367C2B'
                                    size='sm'
                                />}
                            </IconButton>
                        </div>
                    </div>
                </div>
            </AccordionDetails>
        </Accordion>
    );
}

Messaging.propTypes = {
    appUserId: PropTypes.number,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    orgId: PropTypes.number,
    translations: PropTypes.translations,
    userRoles: PropTypes.arrayOf(PropTypes.object)
};

export function mapStateToProps(state) {
    return {
        appUserId: state.account.appUserId,
        membership: state.membership,
        myJdPermissions: state.account.myJdPermissions,
        orgId: state.membership.orgId,
        translations: state.translations,
        userRoles: state.role.userRoles
    };
}

export default connect(mapStateToProps)(Messaging);
